import KanbanAPI from '../../../api/kanban';

const SET_UI_FLAG = 'SET_UI_FLAG';
const SET_BOARDS = 'SET_BOARDS';
const ADD_BOARD = 'ADD_BOARD';
const EDIT_BOARD = 'EDIT_BOARD';
const DELETE_BOARD = 'DELETE_BOARD';
const SET_STEPS = 'SET_STEPS';
const ADD_STEP = 'ADD_STEP';
const EDIT_STEP = 'EDIT_STEP';
const DELETE_STEP = 'DELETE_STEP';
const SET_TASKS = 'SET_TASKS';
const ADD_OR_UPDATE_TASK = 'ADD_OR_UPDATE_TASK';
const DELETE_TASK = 'DELETE_TASK';

export const state = {
  boards: [],
  steps: [],
  tasks: [],
  uiFlags: {
    isFetchingBoards: false,
    isFetchingBoard: false,
    isFetchingTasks: false,
    isCreating: false,
    isUpdating: false,
  },
};

export const getters = {
  getBoards: _state => _state.boards,
  getBoardById: _state => id =>
    _state.boards.find(board => board.id === Number(id)),
  getSteps: _state => [..._state.steps].sort((a, b) => a.position - b.position),
  getTasks: _state => _state.tasks,
  getTasksByStep: _state => stepId =>
    _state.tasks
      .filter(task => task.board_step_id === Number(stepId))
      .sort((a, b) => a.position - b.position),
  getTaskByConversation: _state => conversationId =>
    _state.tasks.find(task =>
      (task.conversation_ids || []).includes(Number(conversationId))
    ),
  getUIFlags: _state => _state.uiFlags,
};

export const actions = {
  async fetchBoards({ commit }, params = {}) {
    commit(SET_UI_FLAG, { isFetchingBoards: true });
    try {
      const response = await KanbanAPI.getBoards(params);
      commit(SET_BOARDS, response.data.boards);
    } finally {
      commit(SET_UI_FLAG, { isFetchingBoards: false });
    }
  },
  async createBoard({ commit }, data) {
    commit(SET_UI_FLAG, { isCreating: true });
    try {
      const response = await KanbanAPI.createBoard(data);
      commit(ADD_BOARD, response.data);
      return response.data;
    } finally {
      commit(SET_UI_FLAG, { isCreating: false });
    }
  },
  async updateBoard({ commit }, { boardId, ...data }) {
    const response = await KanbanAPI.updateBoard(boardId, data);
    commit(EDIT_BOARD, response.data);
    return response.data;
  },
  async deleteBoard({ commit }, boardId) {
    await KanbanAPI.deleteBoard(boardId);
    commit(DELETE_BOARD, boardId);
  },
  async toggleFavorite({ commit }, boardId) {
    const response = await KanbanAPI.toggleFavorite(boardId);
    commit(EDIT_BOARD, response.data);
  },
  async updateBoardAgents({ commit }, { boardId, agentIds }) {
    const response = await KanbanAPI.updateBoardAgents(boardId, agentIds);
    commit(EDIT_BOARD, response.data);
  },
  async updateBoardInboxes({ commit }, { boardId, inboxIds }) {
    const response = await KanbanAPI.updateBoardInboxes(boardId, inboxIds);
    commit(EDIT_BOARD, response.data);
  },
  async fetchBoard({ commit }, boardId) {
    commit(SET_UI_FLAG, { isFetchingBoard: true });
    try {
      const [boardResponse, stepsResponse] = await Promise.all([
        KanbanAPI.getBoard(boardId),
        KanbanAPI.getSteps(boardId),
      ]);
      commit(EDIT_BOARD, boardResponse.data);
      commit(SET_STEPS, stepsResponse.data.steps);
    } finally {
      commit(SET_UI_FLAG, { isFetchingBoard: false });
    }
  },
  async createStep({ commit }, { boardId, step }) {
    const response = await KanbanAPI.createStep(boardId, step);
    commit(ADD_STEP, response.data);
    return response.data;
  },
  async updateStep({ commit }, { boardId, stepId, step }) {
    const response = await KanbanAPI.updateStep(boardId, stepId, step);
    commit(EDIT_STEP, response.data);
  },
  async deleteStep({ commit }, { boardId, stepId }) {
    await KanbanAPI.deleteStep(boardId, stepId);
    commit(DELETE_STEP, stepId);
  },
  async fetchTasks({ commit }, params = {}) {
    commit(SET_UI_FLAG, { isFetchingTasks: true });
    try {
      const response = await KanbanAPI.getTasks(params);
      commit(SET_TASKS, response.data.tasks);
    } finally {
      commit(SET_UI_FLAG, { isFetchingTasks: false });
    }
  },
  async createTask({ commit }, task) {
    const response = await KanbanAPI.createTask(task);
    commit(ADD_OR_UPDATE_TASK, response.data);
    return response.data;
  },
  async updateTask({ commit }, { taskId, ...task }) {
    const response = await KanbanAPI.updateTask(taskId, task);
    commit(ADD_OR_UPDATE_TASK, response.data);
    return response.data;
  },
  async deleteTask({ commit }, taskId) {
    await KanbanAPI.deleteTask(taskId);
    commit(DELETE_TASK, taskId);
  },
  async moveTask(
    { commit, state: _state },
    { taskId, boardStepId, insertBeforeTaskId = null }
  ) {
    // Atualização otimista: move localmente antes da resposta.
    const task = _state.tasks.find(item => item.id === taskId);
    const previous = task ? { ...task } : null;
    if (task) {
      commit(ADD_OR_UPDATE_TASK, { ...task, board_step_id: boardStepId });
    }
    try {
      const response = await KanbanAPI.moveTask(
        taskId,
        boardStepId,
        insertBeforeTaskId
      );
      commit(ADD_OR_UPDATE_TASK, response.data);
    } catch (error) {
      if (previous) commit(ADD_OR_UPDATE_TASK, previous);
      throw error;
    }
  },
  // Handlers de eventos ActionCable
  addOrUpdateTaskFromEvent({ commit }, task) {
    commit(ADD_OR_UPDATE_TASK, task);
  },
  removeTaskFromEvent({ commit }, taskData) {
    commit(DELETE_TASK, taskData.id);
  },
};

export const mutations = {
  [SET_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [SET_BOARDS](_state, boards) {
    _state.boards = boards;
  },
  [ADD_BOARD](_state, board) {
    _state.boards.push(board);
  },
  [EDIT_BOARD](_state, board) {
    const index = _state.boards.findIndex(item => item.id === board.id);
    if (index > -1) {
      _state.boards.splice(index, 1, board);
    } else {
      _state.boards.push(board);
    }
  },
  [DELETE_BOARD](_state, boardId) {
    _state.boards = _state.boards.filter(board => board.id !== boardId);
  },
  [SET_STEPS](_state, steps) {
    _state.steps = steps;
  },
  [ADD_STEP](_state, step) {
    _state.steps.push(step);
  },
  [EDIT_STEP](_state, step) {
    const index = _state.steps.findIndex(item => item.id === step.id);
    if (index > -1) _state.steps.splice(index, 1, step);
  },
  [DELETE_STEP](_state, stepId) {
    _state.steps = _state.steps.filter(step => step.id !== stepId);
  },
  [SET_TASKS](_state, tasks) {
    _state.tasks = tasks;
  },
  [ADD_OR_UPDATE_TASK](_state, task) {
    const index = _state.tasks.findIndex(item => item.id === task.id);
    if (index > -1) {
      _state.tasks.splice(index, 1, task);
    } else {
      _state.tasks.push(task);
    }
  },
  [DELETE_TASK](_state, taskId) {
    _state.tasks = _state.tasks.filter(task => task.id !== taskId);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
